import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const PAGE_SIZES = [25, 50, 100, 200];

/**
 * Paged list fetching.
 *
 * The API returns a plain array and puts the row count in X-Total-Count, so
 * callers that only want a dropdown are unaffected by paging. This hook reads
 * that header and owns page state.
 *
 * `params` must be memo-stable or referentially constant per render — pass a
 * primitive-keyed object built inline and it will refetch forever.
 */
export function usePagedList(path, params = {}, { pageSize = 50, enabled = true } = {}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(params);

  // Any filter change invalidates the current page number: staying on page 4
  // of a result set that now has one page shows an empty screen.
  useEffect(() => { setPage(1); }, [key]);

  const load = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    api
      .get(path, { params: { ...JSON.parse(key), page, page_size: size } })
      .then((res) => {
        setRows(res.data || []);
        const count = res.headers?.["x-total-count"];
        setTotal(count != null ? Number(count) : (res.data || []).length);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [path, key, page, size, enabled]);

  useEffect(() => { load(); }, [load]);

  return { rows, total, page, setPage, size, setSize, loading, reload: load };
}

export default function Pagination({ page, setPage, size, setSize, total, loading, testid }) {
  const pages = Math.max(Math.ceil(total / size), 1);
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(page * size, total);

  // One page and a short list is the common case — no controls needed.
  if (total <= PAGE_SIZES[0] && pages <= 1) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600"
      data-testid={testid || "pagination"}>
      <div>
        {loading ? "Loading…" : <>Showing <strong>{from}–{to}</strong> of <strong>{total}</strong></>}
      </div>
      <div className="flex items-center gap-2">
        <Select value={String(size)} onValueChange={(v) => { setSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="h-8 w-24 text-xs" data-testid="page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(page - 1, 1))}
            disabled={page <= 1}
            data-testid="page-prev"
            className="rounded border border-slate-200 bg-white p-1.5 transition-colors duration-150 hover:border-slate-400 disabled:opacity-40 disabled:hover:border-slate-200"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-2 tabular-nums">{page} / {pages}</span>
          <button
            onClick={() => setPage(Math.min(page + 1, pages))}
            disabled={page >= pages}
            data-testid="page-next"
            className="rounded border border-slate-200 bg-white p-1.5 transition-colors duration-150 hover:border-slate-400 disabled:opacity-40 disabled:hover:border-slate-200"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shared download helper — every export endpoint returns a file blob. */
export async function downloadFile(path, params, fallbackName) {
  const res = await api.get(path, { params, responseType: "blob" });
  const disp = res.headers?.["content-disposition"] || "";
  const match = /filename="?([^"]+)"?/.exec(disp);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
